import { z } from "zod";

const identifierSchema = z.string().trim().min(1).max(200);
const nonEmptyTextSchema = z.string().trim().min(1);

export const presentationCapabilityStatusSchema = z.enum([
  "supported",
  "degraded",
  "unavailable",
]);

const renderCapabilitySchema = z
  .object({
    providerId: nonEmptyTextSchema,
    providerVersion: nonEmptyTextSchema.nullable(),
    available: z.boolean(),
    confidence: z.enum([
      "powerpoint_verified",
      "fallback_preview",
      "structural_only",
    ]),
    features: z
      .object({
        slideImages: z.boolean(),
        pdf: z.boolean(),
        saveAndReopen: z.boolean(),
      })
      .strict(),
    unavailableReason: nonEmptyTextSchema.nullable(),
  })
  .strict();

const brandAssetSchema = z
  .object({
    relativePath: nonEmptyTextSchema,
    kind: z.enum(["logo", "template", "theme", "guideline"]),
    bytes: z.number().int().nonnegative(),
  })
  .strict();

const capabilityFindingSchema = z
  .object({
    code: nonEmptyTextSchema,
    severity: z.enum(["info", "warning", "error"]),
    summary: nonEmptyTextSchema,
    retryable: z.boolean(),
    recoveryAction: nonEmptyTextSchema,
  })
  .strict();

export const presentationCapabilityObservationSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    status: presentationCapabilityStatusSchema,
    summary: nonEmptyTextSchema,
    selectedProviderId: nonEmptyTextSchema.nullable(),
    providers: z.array(renderCapabilitySchema).min(2),
    fonts: z
      .object({
        status: presentationCapabilityStatusSchema,
        familyCount: z.number().int().nonnegative(),
        families: z.array(nonEmptyTextSchema),
        required: z.array(nonEmptyTextSchema),
        missing: z.array(nonEmptyTextSchema),
      })
      .strict(),
    brand: z
      .object({
        status: presentationCapabilityStatusSchema,
        scannedEntries: z.number().int().nonnegative(),
        truncated: z.boolean(),
        assets: z.array(brandAssetSchema),
      })
      .strict(),
    findings: z.array(capabilityFindingSchema),
    nextActions: z.array(nonEmptyTextSchema),
  })
  .strict();

export const presentationCapabilityCommandSchema = z
  .object({
    type: z.literal("inspect_presentation_capabilities"),
    input: z
      .object({
        requiredFonts: z.array(nonEmptyTextSchema).max(20),
        directoryJobId: identifierSchema.optional(),
      })
      .strict(),
  })
  .strict();

export type PresentationCapabilityStatus = z.infer<
  typeof presentationCapabilityStatusSchema
>;
export type PresentationCapabilityObservation = z.infer<
  typeof presentationCapabilityObservationSchema
>;
export type PresentationCapabilityCommand = z.infer<
  typeof presentationCapabilityCommandSchema
>;

export interface PresentationCapabilityCommandPort {
  execute(
    command: PresentationCapabilityCommand,
  ): Promise<PresentationCapabilityObservation>;
}
